import {
  hasApprovalRequired,
  isApprovedDecision,
  isRejectedDecision,
  type IssueWorkflowOutDto,
  type WorkflowDecisionOutDto,
} from './types';

/**
 * Status of the latest workflow decision issued on an issue, used for the
 * "Ostatnia decyzja" (Last decision) grid column, sort and filter.
 *
 * `null` represents N/A: the issue's workflow has no approval-required stages,
 * so the "last decision" concept does not apply.
 */
export const LastDecisionStatus = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  NONE: 'none',
} as const;

export type LastDecisionStatus =
  (typeof LastDecisionStatus)[keyof typeof LastDecisionStatus];

function workflowHasApprovalStages(
  workflow: IssueWorkflowOutDto | undefined
): boolean {
  if (!workflow) {
    return false;
  }

  return workflow.stages
    .filter((stage) => !stage.deleted)
    .some(hasApprovalRequired);
}

/**
 * Returns the most recently created decision, matching the
 * ascending-sort-then-take-last convention used by `getApproverDecisions`
 * in `decisionStatus.ts`.
 */
export function getLatestWorkflowDecision(
  decisions: readonly WorkflowDecisionOutDto[] | null | undefined
): WorkflowDecisionOutDto | undefined {
  if (!decisions?.length) {
    return undefined;
  }

  return [...decisions]
    .sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    .at(-1);
}

/**
 * Computes the "last decision" status of an issue for display/sort/filter purposes.
 * Returns `null` when the issue's workflow has no approval-required stages (N/A).
 *
 * `latestDecision` can be passed in when the caller already computed it (e.g. to
 * also derive `latestDecisionId`), to avoid sorting `issue.workflowDecisions` twice.
 */
export function getLastDecisionStatus(
  issue: { workflowDecisions?: WorkflowDecisionOutDto[] | null },
  workflow: IssueWorkflowOutDto | undefined,
  latestDecision: WorkflowDecisionOutDto | undefined = getLatestWorkflowDecision(
    issue.workflowDecisions
  )
): LastDecisionStatus | null {
  if (!workflowHasApprovalStages(workflow)) {
    return null;
  }

  if (!latestDecision) {
    return LastDecisionStatus.NONE;
  }

  if (isApprovedDecision(latestDecision)) {
    return LastDecisionStatus.APPROVED;
  }

  if (isRejectedDecision(latestDecision)) {
    return LastDecisionStatus.REJECTED;
  }

  return LastDecisionStatus.NONE;
}

